import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button, Form, Input } from 'semantic-ui-react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { uploadFile } from '../../../../service/uploadDownloadService';
import { SIGNATURE_FILE_TYPE } from '../../../../HelperMethods/constants';
import { base64ToArrayBuffer, sleep } from '../../../../HelperMethods/UtilityFunctions';
import logUtils from '../../../../HelperMethods/LogUtils';
import { updateSignature } from '../../../../actions/appActions';
import DropdownModal from './DropdownModal';
import ManualSignatureComponent from './ManualSignature';

const createCanvasForSignature = async (signaturecanvasNode, text) => {
  const canvasNode = document.createElement('canvas');
  const canvasContext = canvasNode.getContext('2d');
  canvasContext.fillStyle = 'black';
  canvasContext.textAlign = 'center';
  const dataUrl = signaturecanvasNode.toDataURL();
  // const { width: currentCanvasWidth, height: currentCanvasHeight } = canvasNode.getBoundingClientRect();
  const { width: currentCanvasWidth, height: currentCanvasHeight } = { width: 300, height: 150 };
  canvasContext.width = currentCanvasWidth;
  canvasContext.height = currentCanvasHeight;
  canvasContext.clearRect(0, 0, currentCanvasWidth, currentCanvasHeight);
  canvasContext.fillStyle = 'white';
  canvasContext.fillRect(0, 0, currentCanvasWidth, currentCanvasHeight);
  const img = new window.Image();
  img.addEventListener('load', function () {
    canvasContext.drawImage(img, 0, 0, currentCanvasWidth, 0.7 * currentCanvasHeight);
  });
  img.setAttribute('src', dataUrl);
  // ------------------------------
  const fontSelected = 'Arial';
  canvasContext.font = `18px ${fontSelected}`;
  canvasContext.textAlign = 'center';
  canvasContext.fillStyle = 'black';
  canvasContext.fillText(text, 0.5 * currentCanvasWidth, 0.85 * currentCanvasHeight);
  canvasContext.font = '14px JoynFontBook';
  canvasContext.fillStyle = 'black';
  canvasContext.textAlign = 'right';
  canvasContext.fillText(moment().format('MM-DD-YYYY h:mmA'), currentCanvasWidth - 5, currentCanvasHeight - 5);
  await sleep(100);
  const newDataUrl = canvasNode.toDataURL('image/png');
  const base64String = newDataUrl.split(',')[1];
  const arrayBuffer = base64ToArrayBuffer(base64String);
  return arrayBuffer;
};

export default function TypeNameDropdownModal({ openModal, setOpenModal, onFileUploaded }) {
  const [text, setText] = useState(``);
  const [errors, setError] = useState({});
  const [canvasWidth, handleCanvasWidth] = useState('528');
  const [canvasHeight, handleCanvasHeight] = useState('160');
  const [uploading, setUploading] = useState(false);
  const [isSigned, handleCheckSign] = useState(false);
  const [showModalHeader, setShowModalHeader] = useState(true);
  const signatureCanvas = useRef(null);
  const dispatch = useDispatch();

  const calculateCanvasWidth = () => {
    const joyn2formElement = document.getElementById('joyn2form');
    if (!joyn2formElement) {
      handleCanvasWidth('528');
      handleCanvasHeight('160');
    } else {
      const calendarDateRect = joyn2formElement.getBoundingClientRect();
      let canvasColWidth = calendarDateRect.right - calendarDateRect.left;
      canvasColWidth = Math.floor(0.95 * canvasColWidth);
      let canvasColHeight = calendarDateRect.bottom - calendarDateRect.top;
      canvasColHeight = Math.floor(0.3 * canvasColHeight);
      console.log('canvasColWidth outside', canvasWidth);
      if (`${canvasColWidth}` !== canvasWidth) {
        handleCanvasWidth(`${canvasColWidth}`);
        console.log('canvasColWidth inside');
      }
      if (`${canvasColHeight}` !== canvasColHeight) {
        handleCanvasHeight(`${canvasColHeight}`);
        console.log('canvasColWidth inside');
      }
    }
  };

  const onSubmitClicked = useCallback(async () => {
    if (!text || !isSigned) {
      setError({ text: text ? '' : 'Name is mandatory', signature: isSigned ? '' : 'Signature is mandatory' });
    } else {
      const arrayBuffer = await createCanvasForSignature(signatureCanvas?.current, text);
      try {
        setUploading(true);
        const uploadObj = await uploadFile({ fileName: 'type-name.png', fileType: 'image/png', arrayBuffer });
        onFileUploaded({ ...uploadObj, fileType: SIGNATURE_FILE_TYPE.IMAGE });
        dispatch(updateSignature(uploadObj));
      } catch (e) {
        logUtils.logError(e, 'Could not upload Image');
      } finally {
        setOpenModal(false);
        setUploading(false);
        setError({});
      }
    }
  }, [isSigned, text, onFileUploaded, setOpenModal, setUploading]);

  useEffect(() => {
    calculateCanvasWidth();
  }, [openModal]);

  useEffect(() => {
    window.addEventListener('orientationchange', () => {
      if (window.orientation === 0 || window.orientation === 180) {
        // The device is in portrait mode.
        console.log('The device is in portrait mode');
        setShowModalHeader(true);
      } else {
        // The device is in landscape mode.
        console.log('The device is in landscape mode');
        setShowModalHeader(false);
      }
    });
  }, []);

  return (
    <DropdownModal
      setOpenModal={setOpenModal}
      openModal={openModal}
      title="Add Signature"
      id="signature-modal"
      showModalHeader={showModalHeader}
      content={
        <Form className="joyn2form" id="joyn2form">
          <Form.Group style={showModalHeader ? {} : { padding: 0, margin: 0 }}>
            <Form.Field>
              <ManualSignatureComponent
                ref={signatureCanvas}
                signetureError={errors?.signature}
                handleCheckSign={handleCheckSign}
                canvasWidth={canvasWidth}
                canvasHeight={canvasHeight}
                showModalHeader={showModalHeader}
              />
            </Form.Field>
          </Form.Group>
          <Input
            label="Full Name"
            placeholder="Full Name"
            onChange={(e) => {
              setText(e.target.value);
            }}
            style={{ width: '70%' }}
          />
          {errors?.text && <div className="errorText">*{errors.text}</div>}
          {/* <div>
            <Form.Field>
              <label>Preview</label>
              <canvas
                ref={onNameCanvasMount}
                width="500"
                height="200"
                style={{ border: '1px solid #000000', borderRadius: '15px' }}
              >
                Your browser does not support type name functionality. Best supported in Chrome, Edge, Firefox and
                Internet Explorer 9+
              </canvas>
            </Form.Field>
          </div> */}
          <div style={{ textAlign: 'right' }}>
            <Button
              disabled={uploading}
              loading={uploading}
              type="button"
              onClick={() => {
                setOpenModal(false);
                setError({});
              }}
              className="new simple"
              style={{ marginRight: '24px' }}
            >
              Cancel
            </Button>
            <Button
              disabled={uploading}
              loading={uploading}
              className="new black"
              type="button"
              onClick={onSubmitClicked}
            >
              Add Signature
            </Button>
          </div>
        </Form>
      }
    />
  );
}

TypeNameDropdownModal.propTypes = {
  openModal: PropTypes.bool.isRequired,
  setOpenModal: PropTypes.func.isRequired,
  loggedInUser: PropTypes.shape({
    userName: PropTypes.string.isRequired,
    lastName: PropTypes.string,
    firstName: PropTypes.string,
    tenantId: PropTypes.string.isRequired,
  }).isRequired,
  onFileUploaded: PropTypes.func.isRequired,
};
